import { Component } from '@theme/component';

/**
 * Announcement banner custom element that allows fading between content.
 * Based on the Slideshow component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  #fitObserver = new ResizeObserver(() => this.#scheduleFit());
  #fitFrame = 0;

  #scheduleFit = () => {
    if (!this.isConnected || this.#fitFrame) return;
    this.#fitFrame = requestAnimationFrame(() => {
      this.#fitFrame = 0;
      this.#fitAnnouncements();
    });
  };

  #observeFit() {
    this.#fitObserver.disconnect();
    for (const slide of this.querySelectorAll('[data-announcement-fit]')) {
      this.#fitObserver.observe(slide);
      for (const child of slide.querySelectorAll(':scope > .announcement-bar__content, :scope > .announcement-bar__timer, :scope > .announcement-bar__extra')) {
        this.#fitObserver.observe(child);
      }
    }
    this.#scheduleFit();
  }

  #fitAnnouncements() {
    for (const slide of this.querySelectorAll('[data-announcement-fit]')) {
      const timer = slide.querySelector(':scope > .announcement-bar__timer');
      if (!matchMedia('(min-width: 750px)').matches) {
        slide.removeAttribute('data-hide-timer');
        slide.removeAttribute('data-stack-extra');
        slide.removeAttribute('data-fit-ready');
        slide.style.removeProperty('--announcement-fit-inset');
        continue;
      }

      const content = slide.querySelector(':scope > .announcement-bar__content');
      const extra = slide.querySelector(':scope > .announcement-bar__extra');
      if (!content) continue;
      const width = slide.getBoundingClientRect().width;
      if (!width) continue;
      const style = getComputedStyle(slide);
      const gap = parseFloat(style.columnGap) || 0;
      const minimumInset = Math.max(16, parseFloat(style.getPropertyValue('--padding-inline-start')) || 0, parseFloat(style.getPropertyValue('--padding-inline-end')) || 0);
      const messageWidth = content.getBoundingClientRect().width;
      const timerWidth = timer?.getBoundingClientRect().width ?? 0;
      const extraWidth = extra?.getBoundingClientRect().width ?? 0;
      const sideSpace = (width - messageWidth) / 2 - gap - minimumInset;
      const stackExtra = extraWidth > sideSpace;
      const hideTimer = timerWidth > sideSpace || stackExtra;
      const sideWidth = Math.max(hideTimer ? 0 : timerWidth, extraWidth);
      const gridWidth = parseFloat(style.getPropertyValue('--announcement-fit-max-width'));
      const gridGutter = parseFloat(style.getPropertyValue('--announcement-fit-grid-gutter')) || 0;
      // Start at the site's content grid; expand toward the viewport only to avoid collisions.
      const preferredInset = gridWidth > 0 ? Math.max(gridGutter, (width - gridWidth) / 2) : width * 0.1;
      const inset = Math.max(minimumInset, Math.min(preferredInset, (width - messageWidth) / 2 - sideWidth - gap));

      slide.style.setProperty('--announcement-fit-inset', `${Math.floor(inset)}px`);
      slide.toggleAttribute('data-hide-timer', hideTimer);
      // For unusually long messages, keep the link accessible on a second row.
      slide.toggleAttribute('data-stack-extra', stackExtra);
      slide.setAttribute('data-fit-ready', '');
    }
  }

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    this.#observeFit();
    document.fonts?.ready.then(this.#scheduleFit);

    this.play();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.suspend();
    this.removeEventListener('mouseenter', this.suspend);
    this.removeEventListener('mouseleave', this.resume);
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    this.#fitObserver.disconnect();
    cancelAnimationFrame(this.#fitFrame);
    this.#fitFrame = 0;
  }

  updatedCallback() {
    super.updatedCallback();
    this.#observeFit();
  }

  next() {
    this.current += 1;
  }

  previous() {
    this.current -= 1;
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    this.suspend();
    if (!this.autoplay || !this.isConnected) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    this.#current = current;

    let relativeIndex = current % (this.refs.slides ?? []).length;
    if (relativeIndex < 0) {
      relativeIndex += (this.refs.slides ?? []).length;
    }

    this.refs.slides?.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', `${index !== relativeIndex}`);
    });
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.suspend() : this.resume());
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
